import type { TipItem } from "../types/tax.types";
import { cn } from "@/lib/utils";

const PRIORITY_CONFIG = {
  high: {
    label: "High",
    bg: "bg-[var(--color-danger-bg)]",
    text: "text-[var(--color-danger-fg)]",
    dot: "bg-[var(--color-expense)]",
  },
  medium: {
    label: "Medium",
    bg: "bg-[var(--color-primary-bg)]",
    text: "text-[var(--color-primary-600)]",
    dot: "bg-[var(--color-primary-400)]",
  },
  low: {
    label: "Low",
    bg: "bg-muted",
    text: "text-muted-foreground",
    dot: "bg-muted-foreground/40",
  },
} as const;

function TipCard({ tip }: { tip: TipItem }) {
  const cfg = PRIORITY_CONFIG[tip.priority];
  return (
    <div className="rounded-[10px] border border-border bg-card p-4 flex gap-3">
      <div className={cn("w-1.5 rounded-full flex-shrink-0 mt-1", cfg.dot)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="text-[14px] font-semibold text-foreground leading-snug">
            {tip.title}
          </div>
          <span
            className={cn(
              "flex-shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full",
              cfg.bg,
              cfg.text,
            )}
          >
            {cfg.label}
          </span>
        </div>
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          {tip.description}
        </p>
        <div className="mt-2 text-[12px] font-semibold text-[var(--color-income)]">
          Est. savings ฿{tip.potentialSaving.toLocaleString()}
        </div>
      </div>
    </div>
  );
}

export default function OptimizationTips({ tips }: { tips: TipItem[] }) {
  if (tips.length === 0) return null;

  return (
    <div className="rounded-[14px] border border-border bg-card shadow-sm p-4 sm:p-5">
      <div className="mb-4">
        <h2 className="text-[15px] font-semibold text-foreground">
          Tax Planning Tips
        </h2>
        <p className="text-[12px] text-muted-foreground mt-0.5">
          Ranked by potential tax savings
        </p>
      </div>
      <div className="space-y-3">
        {tips.map((tip) => (
          <TipCard key={tip.id} tip={tip} />
        ))}
      </div>
    </div>
  );
}
